import React, { useState, useEffect } from 'react';
import { Save, Send, Fuel, Loader2, AlertTriangle, CheckCircle, XCircle, Info, Gauge } from 'lucide-react'; import API_BASE_URL from "../config";
import Swal from 'sweetalert2';

const FUEL_KEYWORDS = ['fuel', 'diesel', 'gasoline', 'petrol', 'oil', 'lubricant', 'kerosene'];
const LOW_STOCK_THRESHOLD = 20;

export default function FuelRequisitionForm() {
  const [formData, setFormData] = useState({
    controlNo: '',
    requestorName: '',       // ✅ blank
    plateNumber: '',
    requestDate: new Date().toISOString().split('T')[0],
    requestTime: new Date().toTimeString().slice(0, 5),
    department: '',
    description: '',
    level: '',
    approvedByVisor: false,
    actualLiters: 0,
    truckOdometer: 0,
    odoDefective: false,
    odoDefectiveNo: '',
    quantityLiters: 0,
    flowMeterBeg: 0,
    flowMeterEnd: 0,
    purpose: '',
    requestedBy: '',         // ✅ blank
    requestedBySignature: '',
    approvedBy: '',
    approvedBySignature: '',
    notedBy: '',
    notedBySignature: '',
    gasManAssistBy: '',
    gasManSignature: ''
  });

  const [users, setUsers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [currentUserSignature, setCurrentUserSignature] = useState('');
  const [errors, setErrors] = useState({});
  const [notedByUserId, setNotedByUserId] = useState('');
  const [approvedByUserId, setApprovedByUserId] = useState('');
  const [plateSearch, setPlateSearch] = useState('');
  const [showPlateDropdown, setShowPlateDropdown] = useState(false);
  // ⛽ Fuel inventory states
  const [fuelInventory, setFuelInventory] = useState([]);
  const [fuelStockWarning, setFuelStockWarning] = useState(null);
  const [totalFuelAvailable, setTotalFuelAvailable] = useState(0);
  const [prevTruckData, setPrevTruckData] = useState(null);

  useEffect(() => {
    fetchData();
    generateControlNo();
  }, []);

  // Auto-select Justin Choi as Noted By
  useEffect(() => {
    const justin = users.find(u => u.firstName === "Justin" && u.lastName === "Choi");
    if (justin) {
      setNotedByUserId(String(justin.id));
      handleUserSelect('notedBy', String(justin.id));
    }
  }, [users]);

  // ⛽ Auto-compute actualLiters
  useEffect(() => {
    const beg = parseFloat(formData.flowMeterBeg) || 0;
    const end = parseFloat(formData.flowMeterEnd) || 0;

    if (end >= beg) {
      const computed = parseFloat((end - beg).toFixed(2));
      setFormData(prev => ({ ...prev, actualLiters: computed }));

      if (computed > 0 && fuelInventory.length > 0) {
        const total = fuelInventory.reduce((s, i) => s + i.availableQty, 0);
        if (total <= 0) setFuelStockWarning('out');
        else if (computed > total) setFuelStockWarning('over');
        else if (total <= LOW_STOCK_THRESHOLD) setFuelStockWarning('low');
        else setFuelStockWarning('ok');
      } else {
        setFuelStockWarning(null);
      }
    } else {
      setFormData(prev => ({ ...prev, actualLiters: 0 }));
      setFuelStockWarning(null);
    }
  }, [formData.flowMeterBeg, formData.flowMeterEnd, fuelInventory]);

  useEffect(() => {
    setTotalFuelAvailable(fuelInventory.reduce((s, i) => s + i.availableQty, 0));
  }, [fuelInventory]);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      // Users
      const usersRes = await fetch(`${API_BASE_URL}api/Users`);
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.filter(u => u.isActive === 1));
      }

      // Positions — try common endpoint names
      try {
        const posRes = await fetch(`${API_BASE_URL}api/Position`);
        if (posRes.ok) {
          const posData = await posRes.json();
          setPositions(posData);
          console.log('📋 Positions:', posData);
        }
      } catch {
        try {
          const posRes2 = await fetch(`${API_BASE_URL}api/Positions`);
          if (posRes2.ok) {
            const posData2 = await posRes2.json();
            setPositions(posData2);
          }
        } catch { /* no positions endpoint, will fallback */ }
      }

      // Departments
      const deptsRes = await fetch(`${API_BASE_URL}api/Department`);
      if (deptsRes.ok) {
        const deptsData = await deptsRes.json();
        setDepartments(deptsData.filter(d => d.isActive === 1));
      }

      // Trucks
      const trucksRes = await fetch(`${API_BASE_URL}api/Truck`);
      if (trucksRes.ok) {
        const trucksData = await trucksRes.json();
        setTrucks(trucksData.map(t => ({
          id: t.id,
          truckPlate: t.truckPlate || t.TruckPlate,
          model: t.model || t.Model,
          level: t.level || t.Level,
        })));
      }

      // ⛽ Fuel Inventory
      // ── Fuel Inventory ──
      const invRes = await fetch(`${API_BASE_URL}api/InventoryData`);
      if (invRes.ok) {
        const invData = await invRes.json();
        const map = new Map();
        invData.forEach(inv => {
          const isFuel = FUEL_KEYWORDS.some(kw =>
            inv.itemDescription?.toLowerCase().includes(kw) ||
            inv.itemCode?.toLowerCase().includes(kw)
          );
          if (!isFuel) return;
          const key = inv.itemCode;
          if (!map.has(key)) {
            map.set(key, {
              itemCode: inv.itemCode,
              itemDescription: inv.itemDescription,
              uom: inv.uom || 'L',
              availableQty: 0,
            });
          }
          // ✅ Quantity ang gamitin, hindi availableQty
          map.get(key).availableQty += Math.max(0, Number(inv.quantity) || 0);
        });
        setFuelInventory(Array.from(map.values()));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load data', confirmButtonColor: '#dc2626' });
    } finally {
      setLoadingData(false);
    }
  };

  // ✅ Auto-fill Approved By based on selected department
  const handleDepartmentChange = async (e) => {
    const selectedDept = e.target.value;
    setFormData(prev => ({
      ...prev,
      department: selectedDept,
      approvedBy: '',
      approvedBySignature: '',
    }));
    setApprovedByUserId('');
    if (errors.department) setErrors(prev => ({ ...prev, department: '' }));
    if (!selectedDept) return;

    // Based sa DB mo: positionId = department name string (e.g. "FG")
    // At DepartmentId = numeric dept ID
    // Kuhanin ang lahat ng users na may positionId === selectedDept
    const deptUsers = users.filter(u => {
      const pos = (u.positionId || u.PositionId || '').toString();
      // Exact match OR positionId starts with the selected dept
      return pos === selectedDept || pos.startsWith(selectedDept + '/') || pos.startsWith(selectedDept + '-');
    });

    console.log(`[Approver] Users with positionId="${selectedDept}":`, deptUsers);

    // Kumuha ng una
    const approverUser = deptUsers[0] || null;

    if (approverUser) {
      const signature = await fetchUserSignature(approverUser.id);
      const fullName = `${approverUser.firstName} ${approverUser.lastName}`;
      setApprovedByUserId(String(approverUser.id));
      setFormData(prev => ({
        ...prev,
        approvedBy: fullName,
        approvedBySignature: signature,
      }));
    }
  };
  const generateControlNo = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const rand = Math.floor(Math.random() * 9000) + 1000;
    setFormData(prev => ({ ...prev, controlNo: `FR-${y}${m}${d}-${rand}` }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      // ✅ Auto-sync: Requestor → Requested By
      if (name === 'requestorName') {
        updated.requestedBy = value;
      }
      return updated;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleTruckSelect = async (e) => {
    const truckId = e.target.value;
    if (!truckId) {
      setFormData(prev => ({ ...prev, plateNumber: '', description: '', level: '' }));
      setPrevTruckData(null);
      return;
    }
    const t = trucks.find(t => t.id === parseInt(truckId));
    if (t) {
      setFormData(prev => ({
        ...prev,
        plateNumber: t.truckPlate,
        description: t.model,
        level: t.level,
      }));

      // ✅ Fetch previous fuel requisition ng truck na ito
      try {
        const res = await fetch(`${API_BASE_URL}api/FuelRequisition`);
        if (res.ok) {
          const allReqs = await res.json();

          // Kunin lahat ng approved records ng plate na ito, sorted by latest
          const truckReqs = allReqs
            .filter(r =>
              r.plateNumber === t.truckPlate &&
              r.truckOdometer > 0 &&
              r.actualLiters > 0 &&
              (r.approvedByVisor === true || r.approvedByVisor === 1)
            )
            .sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate));

          if (truckReqs.length >= 1) {
            const latest = truckReqs[0];
            const prevOdo = latest.truckOdometer;
            const prevLiters = latest.actualLiters;

            // Compute avg km/L from last 2 records kung available
            let avgKmPerLiter = null;
            if (truckReqs.length >= 2) {
              const second = truckReqs[1];
              const kmTraveled = prevOdo - second.truckOdometer;
              if (kmTraveled > 0 && second.actualLiters > 0) {
                avgKmPerLiter = parseFloat((kmTraveled / second.actualLiters).toFixed(2));
              }
            }

            setPrevTruckData({
              prevOdo,
              prevLiters,
              avgKmPerLiter,
              lastDate: latest.requestDate
            });
          } else {
            setPrevTruckData(null);
          }
        }
      } catch (err) {
        console.error('Error fetching truck history:', err);
        setPrevTruckData(null);
      }
    }
  };

  const fetchUserSignature = async (userId) => {
    if (!userId) return '';
    try {
      const res = await fetch(`${API_BASE_URL}api/Users/${userId}/digitalsign`);
      if (res.ok) {
        const blob = await res.blob();
        return await new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }
      return '';
    } catch { return ''; }
  };

  const handleUserSelect = async (field, userId) => {
    if (!userId) {
      setFormData(prev => ({ ...prev, [field]: '', [`${field}Signature`]: '' }));
      return;
    }
    const selectedUser = users.find(u => u.id === parseInt(userId));
    if (selectedUser) {
      const signature = await fetchUserSignature(userId);
      setFormData(prev => ({
        ...prev,
        [field]: `${selectedUser.firstName} ${selectedUser.lastName}`,
        [`${field}Signature`]: signature
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.requestorName) newErrors.requestorName = 'Required';
    if (!formData.plateNumber) newErrors.plateNumber = 'Select a truck';
    if (!formData.department) newErrors.department = 'Required';
    if (!formData.quantityLiters || parseFloat(formData.quantityLiters) <= 0) newErrors.quantityLiters = 'Must be greater than 0';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveLocal = () => {
    const drafts = JSON.parse(localStorage.getItem('fuelRequisitionDrafts') || '[]');
    const existing = drafts.findIndex(d => d.controlNo === formData.controlNo);
    const draft = { ...formData, savedAt: new Date().toISOString(), status: 'draft' };
    if (existing >= 0) drafts[existing] = draft;
    else drafts.push(draft);
    localStorage.setItem('fuelRequisitionDrafts', JSON.stringify(drafts));
    Swal.fire({
      icon: 'success',
      title: '📋 Draft Saved!',
      html: `<strong>${formData.controlNo}</strong><br/><span class="text-gray-500 text-sm">Saved locally as draft</span>`,
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please fill in all required fields before submitting.', confirmButtonColor: '#f97316' });
      return;
    }

    if (fuelStockWarning === 'out') {
      Swal.fire({ icon: 'error', title: '🚫 Out of Stock!', html: `<p>There is <strong>no fuel available</strong> in inventory.</p>`, confirmButtonColor: '#dc2626' });
      return;
    }

    if (fuelStockWarning === 'over') {
      const result = await Swal.fire({
        icon: 'warning', title: '⚠️ Insufficient Fuel Stock!',
        html: `<p>You requested <strong>${formData.actualLiters}L</strong> but only <strong>${totalFuelAvailable.toFixed(2)}L</strong> is available.</p><p class="text-sm text-gray-500 mt-2">Do you still want to submit?</p>`,
        showCancelButton: true, confirmButtonColor: '#f97316', cancelButtonColor: '#6b7280',
        confirmButtonText: 'Submit Anyway', cancelButtonText: 'Cancel'
      });
      if (!result.isConfirmed) return;
    }

    if (fuelStockWarning === 'low') {
      const result = await Swal.fire({
        icon: 'warning', title: '⚡ Low Fuel Stock',
        html: `<p>Only <strong>${totalFuelAvailable.toFixed(2)}L</strong> remaining.</p><p class="text-sm text-gray-500 mt-2">Continue submitting?</p>`,
        showCancelButton: true, confirmButtonColor: '#f97316', cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, Submit', cancelButtonText: 'Cancel'
      });
      if (!result.isConfirmed) return;
    }

    setSubmitting(true);
    try {
      const payload = {
        id: 0,
        controlNo: formData.controlNo,
        requestorName: formData.requestorName,
        plateNumber: formData.plateNumber,
        requestDate: new Date(formData.requestDate).toISOString(),
        requestTime: `${formData.requestTime}:00`,
        department: formData.department,
        description: formData.description || null,
        approvedByVisor: formData.approvedByVisor,
        level: formData.level || null,
        actualLiters: parseFloat(formData.actualLiters) || null,
        truckOdometer: parseInt(formData.truckOdometer) || null,
        odo_Defective: formData.odoDefective || false,
        odo_DefectiveNo: formData.odoDefective ? (formData.odoDefectiveNo || null) : null, quantityLiters: parseFloat(formData.quantityLiters) || null,
        flowMeterBeg: parseFloat(formData.flowMeterBeg) || null,
        flowMeterEnd: parseFloat(formData.flowMeterEnd) || null,
        purpose: formData.purpose || null,
        requestedBy: formData.requestedBy || null,
        requestedBySignature: null,
        approvedBy: formData.approvedBy || null,
        approvedBySignature: null,
        notedBy: formData.notedBy || null,
        notedBySignature: null,
        gasManAssistBy: formData.gasManAssistBy || null,
        gasManSignature: null,
        dateCreated: new Date().toISOString()
      };

      const response = await fetch(`${API_BASE_URL}api/FuelRequisition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        await fetch(`${API_BASE_URL}api/Logs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "CREATE", module: "Fuel Requisition",
            description: `Submitted Fuel Requisition #${formData.controlNo} for ${formData.requestorName} — Plate: ${formData.plateNumber}, Dept: ${formData.department}, Liters: ${formData.actualLiters}`,
            userId: user?.id || user?.userId, username: user?.username,
            fullName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.username,
          }),
        }).catch(() => { });

        const drafts = JSON.parse(localStorage.getItem('fuelRequisitionDrafts') || '[]');
        localStorage.setItem('fuelRequisitionDrafts', JSON.stringify(drafts.filter(d => d.controlNo !== formData.controlNo)));

        Swal.fire({ icon: 'success', title: '✅ Submitted!', html: `<strong>${formData.controlNo}</strong><br/>Fuel requisition submitted successfully`, timer: 2500, showConfirmButton: false });
        resetForm();
        fetchData();
      } else {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData.errors || errorData));
      }
    } catch (error) {
      console.error('❌ Submit error:', error);
      Swal.fire({ icon: 'error', title: 'Submit Failed', text: error.message || 'Failed to submit form', confirmButtonColor: '#dc2626' });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      controlNo: '',
      requestorName: '',
      plateNumber: '',
      requestDate: new Date().toISOString().split('T')[0],
      requestTime: new Date().toTimeString().slice(0, 5),
      department: '',
      approvedByVisor: false,
      description: '',
      level: '',
      actualLiters: 0,
      truckOdometer: 0,
      odoDefective: false,
      odoDefectiveNo: '',
      quantityLiters: 0,
      flowMeterBeg: 0,
      flowMeterEnd: 0,
      purpose: '',
      requestedBy: '',
      requestedBySignature: '',
      approvedBy: '',
      approvedBySignature: '',
      notedBy: '',
      notedBySignature: '',
      gasManAssistBy: '',
      gasManSignature: ''
    });
    setApprovedByUserId('');
    setFuelStockWarning(null);
    setErrors({});
    generateControlNo();
    setPlateSearch('');
    setShowPlateDropdown(false);
  };

  const allowedDepartments = ["FG", "RM", "Logistics", "General Services"];
  const TruckIcon = ({ size = 20 }) => (
    <svg width={size} height={size * 0.6} viewBox="0 0 150 90" fill="none">
      {/* Cargo body */}
      <rect x="0" y="8" width="100" height="52" rx="4" fill="#2563eb" />
      <line x1="50" y1="10" x2="50" y2="58" stroke="#1d4ed8" strokeWidth="1.5" />
      <rect x="44" y="34" width="12" height="3" rx="1.5" fill="#93c5fd" />
      {/* Cab */}
      <rect x="100" y="20" width="50" height="40" rx="5" fill="#1d4ed8" />
      {/* Window */}
      <rect x="106" y="26" width="28" height="18" rx="3" fill="#bfdbfe" />
      <rect x="108" y="28" width="9" height="6" rx="1.5" fill="white" opacity="0.35" />
      {/* Headlight */}
      <rect x="146" y="30" width="6" height="5" rx="1.5" fill="#fde68a" />
      {/* Grill */}
      <rect x="144" y="36" width="6" height="16" rx="2" fill="#1e40af" />
      {/* Cab-body joint */}
      <rect x="98" y="18" width="4" height="44" rx="1" fill="#1e3a8a" />
      {/* Undercarriage */}
      <rect x="6" y="58" width="136" height="7" rx="3" fill="#1e3a8a" />
      {/* Rear wheel */}
      <circle cx="29" cy="70" r="13" fill="#1e293b" />
      <circle cx="29" cy="70" r="8" fill="#334155" />
      <circle cx="29" cy="70" r="4" fill="#94a3b8" />
      {/* Front wheel */}
      <circle cx="121" cy="70" r="13" fill="#1e293b" />
      <circle cx="121" cy="70" r="8" fill="#334155" />
      <circle cx="121" cy="70" r="4" fill="#94a3b8" />
    </svg>
  );
  // ── Fuel Stock Banner ─────────────────────────────────────────────────────
  const FuelStockBanner = () => {
    if (!fuelInventory.length) return (
      <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-6">
        <Info className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className="text-xs text-gray-500 font-medium">No fuel items found in inventory</span>
      </div>
    );

    const bgClass =
      fuelStockWarning === 'out' ? 'bg-red-50 border-red-300' :
        fuelStockWarning === 'over' ? 'bg-red-50 border-red-300' :
          fuelStockWarning === 'low' ? 'bg-amber-50 border-amber-300' :
            fuelStockWarning === 'ok' ? 'bg-emerald-50 border-emerald-300' :
              'bg-blue-50 border-blue-200';
    const icon =
      fuelStockWarning === 'out' ? <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" /> :
        fuelStockWarning === 'over' ? <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" /> :
          fuelStockWarning === 'low' ? <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" /> :
            fuelStockWarning === 'ok' ? <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" /> :
              <Info className="w-5 h-5 text-blue-400 flex-shrink-0" />;

    const message =
      fuelStockWarning === 'out' ? 'OUT OF STOCK — No fuel available in inventory!' :
        fuelStockWarning === 'over' ? `INSUFFICIENT STOCK — Requested ${formData.actualLiters}L exceeds available ${totalFuelAvailable.toFixed(2)}L` :
          fuelStockWarning === 'low' ? `LOW STOCK — Only ${totalFuelAvailable.toFixed(2)}L remaining` :
            fuelStockWarning === 'ok' ? `Stock OK — ${totalFuelAvailable.toFixed(2)}L available` :
              `Fuel inventory: ${totalFuelAvailable.toFixed(2)}L total available`;

    const textClass =
      fuelStockWarning === 'out' || fuelStockWarning === 'over' ? 'text-red-800' :
        fuelStockWarning === 'low' ? 'text-amber-800' :
          fuelStockWarning === 'ok' ? 'text-emerald-800' : 'text-blue-700';

    return (
      <div className={`flex items-start gap-3 border rounded-xl px-4 py-3 mb-6 ${bgClass}`}>
        {icon}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold ${textClass}`}>{message}</p>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {fuelInventory.map((f, i) => (
              <span key={i} className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${f.availableQty <= 0 ? 'bg-red-100 text-red-700 border-red-200' :
                f.availableQty <= LOW_STOCK_THRESHOLD ? 'bg-amber-100 text-amber-700 border-amber-200' :
                  'bg-white text-gray-600 border-gray-200'
                }`}>
                ⛽ {f.itemDescription}: <strong>{f.availableQty.toFixed(2)} {f.uom}</strong>
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading form data...</p>
        </div>
      </div>
    );
  }

  const inputCls = (name) =>
    `w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none transition ${errors[name] ? 'border-red-400 bg-red-50' : 'border-gray-300'
    }`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 p-6">
      <div className="max-w-6xl mx-auto">

        {/* ── HEADER ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-orange-100 to-orange-100 p-4 rounded-xl shadow-lg">
              <TruckIcon size={36} />            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Fuel Requisition Form</h1>
              <p className="text-gray-500 mt-1">Document Code: SPR-F-009</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs text-gray-400 font-semibold uppercase mb-1">Control #</p>
              <p className="font-mono font-bold text-orange-700 text-lg">{formData.controlNo}</p>
            </div>
          </div>
        </div>

        {/* ── FORM ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6 space-y-6">

        

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Control #</label>
              <input type="text" value={formData.controlNo} readOnly
                className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 font-mono font-bold text-orange-900" />
            </div>

            {/* ✅ Requestor — blank, manual input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Requestor <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="requestorName"
                value={formData.requestorName}
                onChange={handleInputChange}
                placeholder="Enter requestor name"
                className={inputCls('requestorName')}
              />
              {errors.requestorName && <p className="text-red-500 text-xs mt-1">{errors.requestorName}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Plate # <span className="text-red-500">*</span>
              </label>
              {/* SAP-style searchable plate input */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search plate number..."
                  value={plateSearch}
                  onChange={(e) => {
                    setPlateSearch(e.target.value);
                    setShowPlateDropdown(true);
                    if (!e.target.value) {
                      setFormData(prev => ({ ...prev, plateNumber: '', description: '', level: '' }));
                      setPrevTruckData(null);
                    }
                  }}
                  onFocus={() => setShowPlateDropdown(true)}
                  onBlur={() => setTimeout(() => setShowPlateDropdown(false), 200)}
                  className={inputCls('plateNumber')}
                />
                {showPlateDropdown && (
                  <ul className="absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                    {trucks
                      .filter(t => t.truckPlate?.toLowerCase().includes(plateSearch.toLowerCase()))
                      .length === 0 ? (
                      <li className="px-4 py-2 text-sm text-gray-400">No results found</li>
                    ) : (
                      trucks
                        .filter(t => t.truckPlate?.toLowerCase().includes(plateSearch.toLowerCase()))
                        .map(t => (
                          <li
                            key={t.id}
                            onMouseDown={() => {
                              setPlateSearch(t.truckPlate);
                              setShowPlateDropdown(false);
                              handleTruckSelect({ target: { value: t.id } });
                            }}
                            className="px-4 py-2 text-sm hover:bg-orange-50 hover:text-orange-700 cursor-pointer font-medium border-b border-gray-100 last:border-0"
                          >
                            <TruckIcon size={18} /> {t.truckPlate}

                            {t.model && <span className="text-gray-400 ml-2 font-normal">— {t.model}</span>}
                          </li>
                        ))
                    )}
                  </ul>
                )}
              </div>
              {errors.plateNumber && <p className="text-red-500 text-xs mt-1">{errors.plateNumber}</p>}
              {formData.plateNumber && (
                <p className="text-xs text-green-600 font-semibold mt-1">✓ {formData.plateNumber}</p>
              )}
            </div>
          </div>

          {/* Date / Time / Department */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
              <input type="date" name="requestDate" value={formData.requestDate}
                onChange={handleInputChange} className={inputCls('requestDate')} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Time</label>
              <input type="time" name="requestTime" value={formData.requestTime}
                onChange={handleInputChange} className={inputCls('requestTime')} />
            </div>

            {/* ✅ Department — triggers Approved By auto-fill */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Department <span className="text-red-500">*</span>
              </label>
              <select name="department" value={formData.department}
                onChange={handleDepartmentChange} className={inputCls('department')}>
                <option value="">Select Department</option>
                {departments
                  .filter(d => allowedDepartments.includes(d.departmentName))
                  .map(d => (
                    <option key={d.id} value={d.departmentName}>{d.departmentName}</option>
                  ))}
              </select>
              {errors.department && <p className="text-red-500 text-xs mt-1">{errors.department}</p>}
            </div>
          </div>

          {/* Vehicle & Fuel Details */}
          <div className="border-2 border-gray-200 rounded-xl p-6 space-y-4">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide">Vehicle & Fuel Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <input type="text" name="description" value={formData.description}
                  onChange={handleInputChange} placeholder="Auto-filled from truck"
                  className={inputCls('description')} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Level</label>
                <input type="text" name="level" value={formData.level}
                  onChange={handleInputChange} placeholder="Auto-filled from truck"
                  className={inputCls('level')} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Actual Liters</label>
                <input type="number" value={formData.actualLiters} readOnly
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-100 font-bold text-orange-700" />
                <p className="text-xs text-gray-400 mt-1">Auto from flow meter</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Truck Odometer</label>
                <input
                  type="number"
                  name="truckOdometer"
                  value={formData.odoDefective ? '' : formData.truckOdometer}
                  onChange={handleInputChange}
                  placeholder={formData.odoDefective ? 'N/A — ODO Defective' : '0'}
                  disabled={formData.odoDefective}
                  className={`${inputCls('truckOdometer')} ${formData.odoDefective
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                    : ''
                    }`}
                />

                {/* ✅ Odo Defective Checkbox */}
                {/* ✅ Odo Defective Checkbox */}
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="odoDefective"
                    checked={formData.odoDefective || false}
                    onChange={(e) =>
                      setFormData(prev => ({
                        ...prev,
                        odoDefective: e.target.checked,
                        odoDefectiveNo: e.target.checked ? prev.odoDefectiveNo : '',
                        truckOdometer: e.target.checked ? 0 : prev.truckOdometer,
                      }))
                    }
                    className="w-4 h-4 accent-orange-500 cursor-pointer"
                  />
                  <label htmlFor="odoDefective" className="text-xs font-semibold text-gray-600 cursor-pointer select-none">
                    <Gauge className="w-5 h-5 text-gray-500" /> Truck ODO Defective?
                  </label>
                </div>

                {/* ✅ Defective No — shows only when checked */}
                {formData.odoDefective && (
                  <input
                    type="text"
                    name="odoDefectiveNo"
                    value={formData.odoDefectiveNo || ''}
                    onChange={handleInputChange}
                    placeholder="Enter defective ref no. (optional)"
                    className="mt-2 w-full px-3 py-2 border border-amber-400 bg-amber-50 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none transition"
                  />
                )}

                {/* ✅ Previous odo reference */}
                {/* ✅ Estimated next odo — kahit 1 record pa lang */}
                {prevTruckData && formData.actualLiters > 0 && (
                  <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 space-y-1">

                    <p className="text-xs text-blue-700 font-semibold">
                      📍 Prev Odo: <span className="font-bold">{prevTruckData.prevOdo.toLocaleString()} km</span>
                      <span className="text-blue-400 ml-2">
                        ({new Date(prevTruckData.lastDate).toLocaleDateString()})
                      </span>
                    </p>

                    {/* Kung may avg km/L — exact estimate */}
                    {prevTruckData.avgKmPerLiter ? (
                      <>
                        <p className="text-xs text-blue-600">
                          ⛽ Avg Efficiency: <span className="font-bold">{prevTruckData.avgKmPerLiter} km/L</span>
                        </p>
                        <p className="text-xs text-emerald-700 font-bold">
                          🔮 Estimated Next Odo: ~{Math.round(
                            prevTruckData.prevOdo + (formData.actualLiters * prevTruckData.avgKmPerLiter)
                          ).toLocaleString()} km
                        </p>
                      </>
                    ) : (
                      /* Kung 1 record pa lang — gamitin default 4 km/L */
                      <>
                        <p className="text-xs text-amber-600">
                          ⚠️ Using default 4 km/L (need 2+ records for accurate estimate)
                        </p>
                        <p className="text-xs text-emerald-700 font-bold">
                          🔮 Estimated Next Odo: ~{Math.round(
                            prevTruckData.prevOdo + (formData.actualLiters * 4)
                          ).toLocaleString()} km
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Kung walang prev data */}
                {!prevTruckData && formData.plateNumber && (
                  <p className="text-xs text-gray-400 mt-1">
                    📭 No previous records found for this truck
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Quantity of Liters Requested <span className="text-red-500">*</span>
              </label>
              <input type="number" name="quantityLiters" value={formData.quantityLiters}
                onChange={handleInputChange} placeholder="0.00" step="0.01"
                className={inputCls('quantityLiters')} />
              {errors.quantityLiters && <p className="text-red-500 text-xs mt-1">{errors.quantityLiters}</p>}
            </div>
          </div>

          {/* Flow Meter */}
          <div className="border-2 border-gray-200 rounded-xl p-6">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">Flow Meter Reading</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Flow Meter Beg.</label>
                <input type="number" name="flowMeterBeg" value={formData.flowMeterBeg}
                  onChange={handleInputChange} placeholder="0.00" step="0.01" className={inputCls('flowMeterBeg')} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Flow Meter End.</label>
                <input type="number" name="flowMeterEnd" value={formData.flowMeterEnd}
                  onChange={handleInputChange} placeholder="0.00" step="0.01" className={inputCls('flowMeterEnd')} />
              </div>
              <div className="flex items-center justify-center">
                <div className={`w-full text-center rounded-xl px-4 py-3 border-2 font-bold text-lg ${fuelStockWarning === 'out' || fuelStockWarning === 'over' ? 'bg-red-50 border-red-300 text-red-700' :
                  fuelStockWarning === 'low' ? 'bg-amber-50 border-amber-300 text-amber-700' :
                    fuelStockWarning === 'ok' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' :
                      'bg-orange-50 border-orange-200 text-orange-700'
                  }`}>
                  <p className="text-xs font-semibold text-gray-400 mb-1">Actual Liters</p>
                  <p>{formData.actualLiters} L</p>
                </div>
              </div>
            </div>
          </div>

          {/* Purpose */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Purpose</label>
            <textarea name="purpose" value={formData.purpose} onChange={handleInputChange}
              rows={3} placeholder="Enter purpose..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none resize-none transition" />
          </div>

          {/* Approvals */}
          <div className="border-2 border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Approvals</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* ✅ Requested By — blank, manual input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Requested by</label>
                <input
                  type="text"
                  name="requestedBy"
                  value={formData.requestedBy}
                  onChange={handleInputChange}
                  placeholder="Auto-filled from Requestor"
                  readOnly
                  className={inputCls('requestedBy')}
                />
                {formData.requestedBy && (
                  <p className="mt-1 text-xs font-semibold text-green-700">✓ {formData.requestedBy}</p>
                )}
                {currentUserSignature && (
                  <div className="mt-2 border border-gray-200 rounded-lg p-2 bg-white inline-block">
                    <img src={currentUserSignature} alt="Signature" className="h-10 object-contain" />
                  </div>
                )}
              </div>

              {/* Noted By */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Noted By</label>
                <select value={notedByUserId}
                  onChange={(e) => { setNotedByUserId(e.target.value); handleUserSelect('notedBy', e.target.value); }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 bg-white focus:outline-none">
                  <option value="">Select User</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                  ))}
                </select>
                {formData.notedBy && (
                  <p className="mt-1 text-xs font-semibold text-green-700">✓ {formData.notedBy}</p>
                )}
              </div>

              {/* ✅ Approved By — auto-filled when department is selected */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Approved by
                  {formData.department && !formData.approvedBy && (
                    <span className="ml-2 text-xs text-amber-500 font-normal">(No approver found for this dept)</span>
                  )}
                </label>
                <select
                  value={approvedByUserId}
                  onChange={async (e) => {
                    setApprovedByUserId(e.target.value);
                    await handleUserSelect('approvedBy', e.target.value);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 bg-white focus:outline-none"
                >
                  <option value="">
                    {formData.department ? 'Select Approver' : 'Select a department first'}
                  </option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                  ))}
                </select>
                {formData.approvedBy && (
                  <p className="mt-1 text-xs font-semibold text-green-700">✓ Auto-filled: {formData.approvedBy}</p>
                )}
                {formData.approvedBySignature && (
                  <div className="mt-2 border border-gray-200 rounded-lg p-2 bg-white inline-block">
                    <img src={formData.approvedBySignature} alt="Approver Signature" className="h-10 object-contain" />
                  </div>
                )}
              </div>

              {/* Gas Man */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Gas Man / Assist by</label>
                <input type="text" placeholder="Enter name"
                  value={formData.gasManAssistBy || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, gasManAssistBy: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none transition" />
                {formData.gasManAssistBy && (
                  <p className="mt-1 text-xs font-semibold text-green-700">✓ {formData.gasManAssistBy}</p>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* ── ACTION BUTTONS ── */}
        <div className="flex justify-end gap-3">
          <button onClick={handleSaveLocal}
            className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 px-6 py-3.5 rounded-xl font-bold border-2 border-gray-300 hover:border-gray-400 transition-all shadow-sm">
            <Save className="w-5 h-5" />
            Save Draft
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            className={`flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold transition-all shadow-lg disabled:opacity-50 text-white ${fuelStockWarning === 'out' || fuelStockWarning === 'over'
              ? 'bg-red-500 hover:bg-red-600'
              : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'
              }`}>
            {submitting ? (
              <><Loader2 className="w-5 h-5 animate-spin" />Submitting...</>
            ) : (
              <><Send className="w-5 h-5" />Submit</>
            )}
          </button>
        </div>

      </div>
    </div>
  );
}